/* global Office Word, console */
import {
  getAccessToken,
  getRefreshToken,
  saveAccessToken,
  saveRefreshToken,
} from "../utils/storage.utils";
import { refreshToken } from "../services/auth.service";
import { aiDraft, AiDraftRequest, AiDraftOption } from "../services/ai.service";
import { convertMarkdownToHtml, isHtmlContent, stripMarkdown } from "../utils/markdown.utils";
import { processDraftFullDocument } from "./draft-full-document";
import { TOAST_STORAGE_KEY } from "../config/storage.config";
import DOMPurify from "dompurify";

const APP_URL = process.env.APP_URL ?? "https://localhost:3000";

export interface ToastMessage {
  type: "info" | "error" | "success" | "warning";
  message: string;
}

export function isUnauthorizedError(error: unknown): boolean {
  if (typeof error !== "object" || error == null || !("response" in error)) return false;
  const resp = error.response;
  return typeof resp === "object" && resp != null && "status" in resp && resp.status === 401;
}

export function sendMessageToTaskpane(message: ToastMessage): void {
  try {
    sessionStorage.setItem(
      TOAST_STORAGE_KEY,
      JSON.stringify({
        ...message,
        timestamp: Date.now(),
      })
    );
  } catch (e) {
    console.warn("[AILA] Could not write to sessionStorage:", e);
  }
}

export function getSelectedText(): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    Office.context.document.getSelectedDataAsync(Office.CoercionType.Text, (result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        resolve(String(result.value ?? ""));
      } else {
        reject(new Error(result.error?.message || "Failed to get selected text"));
      }
    });
  });
}

export async function openTaskpane(): Promise<void> {
  try {
    await Office.addin.showAsTaskpane();
  } catch (error) {
    console.error("Failed to show taskpane:", error);
  }
}
let dialog: Office.Dialog | null = null;

export function showLoadingDialog(): Promise<boolean> {
  return new Promise((resolve) => {
    const loadingUrl = `${APP_URL}/loading.html`;

    Office.context.ui.displayDialogAsync(
      loadingUrl,
      { height: 50, width: 50, displayInIframe: true },
      (asyncResult) => {
        if (asyncResult.status === Office.AsyncResultStatus.Failed) {
          console.warn("[AILA] Could not show loading dialog:", asyncResult.error.message);
          resolve(false);
        } else {
          dialog = asyncResult.value;
          dialog.addEventHandler(Office.EventType.DialogMessageReceived, () => {});
          resolve(true);
        }
      }
    );
  });
}

export function closeLoadingDialog(): void {
  if (dialog) {
    try {
      dialog.close();
    } catch {
      // Dialog may already have been dismissed by the user or by Office.js.
    } finally {
      dialog = null;
    }
  }
}

export async function validatePrerequisites(
  _option: AiDraftOption,
  completeEvent: () => void
): Promise<string | null> {
  const officeInfo = await Office.onReady();
  if (officeInfo.host == null) {
    console.error("[AILA] Office.js not ready");
    completeEvent();
    return null;
  }
  if (officeInfo.host !== Office.HostType.Word || typeof Word === "undefined") {
    console.error("[AILA] Word API not ready");
    completeEvent();
    return null;
  }
  let token = await getAccessToken();
  if (token == null) {
    const refresh = await getRefreshToken();
    if (refresh != null) {
      try {
        const tokens = await refreshToken();
        token = tokens.access_token;
        await saveAccessToken(tokens.access_token);
        await saveRefreshToken(tokens.refresh_token);
      } catch (refreshError) {
        console.error("[AILA] Token refresh failed:", refreshError);
      }
    }

    if (token == null) {
      console.log("[AILA] No valid token. Opening taskpane for login.");
      await openTaskpane();
      sendMessageToTaskpane({ type: "error", message: "Please log in to use this feature." });
      completeEvent();
      return null;
    }
  }
  let selectedText = "";
  try {
    selectedText = await getSelectedText();
  } catch (selectionError) {
    console.error("[AILA] Error getting selected text:", selectionError);
    completeEvent();
    return null;
  }
  if (!selectedText || selectedText.trim().length === 0) {
    sendMessageToTaskpane({
      type: "info",
      message: "Please select some text first, then right-click to use AILA features.",
    });
    completeEvent();
    return null;
  }
  return selectedText;
}

export async function insertContentIntoDocument(htmlContent: string): Promise<void> {
  if (typeof Word === "undefined") {
    throw new Error("Word API not available");
  }

  try {
    await Word.run(async (context) => {
      const range = context.document.getSelection();
      const sanitizedHtml = DOMPurify.sanitize(htmlContent);
      range.insertHtml(sanitizedHtml, Word.InsertLocation.replace);
      await context.sync();
    });
  } catch (wordError) {
    console.error("[AILA] Failed to insert HTML, falling back to plain text:", wordError);
    try {
      const plainText = stripMarkdown(htmlContent);
      await Word.run(async (context) => {
        const range = context.document.getSelection();
        range.insertText(plainText, Word.InsertLocation.replace);
        await context.sync();
      });
    } catch (textError) {
      console.error("[AILA] Failed to insert plain text:", textError);
      throw new Error("Failed to insert content into document");
    }
  }
}

export async function executeAiDraft(
  option: AiDraftOption,
  selectedText: string,
  completeEvent: () => void
): Promise<void> {
  await showLoadingDialog();
  try {
    const { getDocumentInfo } = await import("../utils/document.utils");
    const documentInfo = await getDocumentInfo();
    const documentId = documentInfo.id;
    const request: AiDraftRequest = { document_id: documentId ?? "", text_selected: selectedText };
    const isDraftFullDocument = option === AiDraftOption.DraftFullDocument;
    const response = isDraftFullDocument
      ? await processDraftFullDocument(request)
      : await aiDraft(option, request);
    if (!response.response || response.response.trim().length === 0) {
      console.warn("[AILA] AI returned an empty response. Selection preserved.");
      closeLoadingDialog();
      sendMessageToTaskpane({
        type: "warning",
        message: "The AI returned an empty response. Your text was not modified.",
      });
      completeEvent();
      return;
    }
    const htmlResponse = isHtmlContent(response.response)
      ? response.response
      : convertMarkdownToHtml(response.response);
    await insertContentIntoDocument(htmlResponse);
    closeLoadingDialog();
    // DraftFullDocument may return partial content if an aiDraft call fails
    // mid-loop or the safety fuse trips. Flag it so the user doesn't trust a
    // silently truncated legal document.
    if (
      isDraftFullDocument &&
      "truncated" in response &&
      (response as { truncated?: boolean }).truncated === true
    ) {
      sendMessageToTaskpane({
        type: "warning",
        message:
          "Draft inserted, but the AI stopped before finishing. Review the document for completeness.",
      });
    }
    completeEvent();
  } catch (apiError: unknown) {
    console.error("[AILA] API Error:", apiError);
    closeLoadingDialog();
    if (isUnauthorizedError(apiError)) {
      await openTaskpane();
      sendMessageToTaskpane({
        type: "error",
        message: "Your session has expired. Please log in again.",
      });
    } else {
      console.error(
        "[AILA] Draft error detail:",
        apiError instanceof Error ? apiError.message : apiError
      );
      sendMessageToTaskpane({ type: "error", message: "Something went wrong. Please try again." });
    }
    completeEvent();
  }
}
